import React, { useCallback, useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, CalendarIcon, BookIcon, CheckIcon, SearchIcon, PlusIcon } from 'lucide-react';
import { useTheme } from './providers/ThemeProvider';
import { toast } from 'react-hot-toast';
import { getTeacherStudents } from '../utils/teacherUtils';
import { addStudentToTeacher } from '../services/studentService';
interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTask?: (taskData: any) => void;
  darkMode?: boolean;
  teacherId?: string;
  lessonId?: string;
  studentId?: string;
}
interface Student {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  instrument?: string;
}
export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onAddTask,
  darkMode: darkModeProp,
  teacherId = '',
  lessonId,
  studentId: preselectedStudentId
}) => {
  // Get theme from context
  const {
    theme
  } = useTheme();
  const isDark = darkModeProp !== undefined ? darkModeProp : theme === 'dark';
  // Form state
  const [title, setTitle] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  // Student picker state
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [isAddingNewStudent, setIsAddingNewStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const studentDropdownRef = useRef<HTMLDivElement>(null);
  const studentSearchInputRef = useRef<HTMLInputElement>(null);
  // Validation state
  const [errors, setErrors] = useState<{
    title?: string;
    student?: string;
    dueDate?: string;
  }>({});
  // Focus management
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Add drag state - OPTIMIZED: Use refs for drag offset to avoid re-renders
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({
    x: 0,
    y: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const modalRef = useRef<HTMLDivElement>(null);
  // Load students from Firestore
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  useEffect(() => {
    const fetchStudents = async () => {
      if (isOpen && teacherId) {
        setIsLoadingStudents(true);
        try {
          const fetchedStudents = await getTeacherStudents(teacherId);
          // Filter for active students only
          const activeStudents = fetchedStudents.filter((student: any) => !student.status || student.status === 'active' || student.status === 'trial' || student.status === 'pending');
          setStudents(activeStudents);
        } catch (error) {
          console.error('Error loading students:', error);
          toast.error('Failed to load students');
        } finally {
          setIsLoadingStudents(false);
        }
      }
    };
    fetchStudents();
  }, [isOpen, teacherId]);
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setSelectedStudent(preselectedStudentId || '');
      setDueDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setReference('');
      setErrors({});
      setStudentSearch('');
      setIsAddingNewStudent(false);
      // Focus the title input when modal opens
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, preselectedStudentId]);
  // Close student dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target as Node)) {
        setIsStudentDropdownOpen(false);
        setIsAddingNewStudent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Focus search input when student dropdown opens
  useEffect(() => {
    if (isStudentDropdownOpen && studentSearchInputRef.current) {
      setTimeout(() => {
        studentSearchInputRef.current?.focus();
      }, 50);
    }
  }, [isStudentDropdownOpen]);
  // Initialize modal position to center on mount
  useEffect(() => {
    if (isOpen && modalRef.current && window.innerWidth >= 768) {
      const modalWidth = 512; // max-w-lg = 32rem = 512px
      const modalHeight = modalRef.current.offsetHeight || 600;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = (viewportWidth - modalWidth) / 2;
      const top = (viewportHeight - modalHeight) / 2;
      setModalPosition({
        top,
        left
      });
      // Reset drag offset on every open
      dragOffsetRef.current = {
        x: 0,
        y: 0
      };
    }
  }, [isOpen]);
  // Add drag handlers - OPTIMIZED: Use transform + RAF like CreateLessonModal
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Disable drag on mobile
    if (window.innerWidth < 768) return;
    if (!modalRef.current) return;
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  }, []);
  const handleDrag = useCallback((e: MouseEvent) => {
    if (!modalRef.current) return;
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    dragOffsetRef.current = {
      x: deltaX,
      y: deltaY
    };
    // Cancel previous RAF if still pending
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    // Schedule DOM update on next frame
    rafIdRef.current = requestAnimationFrame(() => {
      if (modalRef.current) {
        const finalTop = modalPosition.top + dragOffsetRef.current.y;
        const finalLeft = modalPosition.left + dragOffsetRef.current.x;
        modalRef.current.style.transform = `translate(${finalLeft - modalPosition.left}px, ${finalTop - modalPosition.top}px)`;
      }
    });
  }, [dragStartPos, modalPosition]);
  const handleDragEnd = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Calculate new position with bounds checking
    const modalWidth = modalRef.current?.offsetWidth || 512;
    const modalHeight = modalRef.current?.offsetHeight || 600;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newLeft = modalPosition.left + dragOffsetRef.current.x;
    let newTop = modalPosition.top + dragOffsetRef.current.y;
    // Constrain to viewport bounds
    newLeft = Math.max(0, Math.min(newLeft, viewportWidth - modalWidth));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - modalHeight));
    // CRITICAL: Apply final position to DOM synchronously BEFORE clearing transform
    // This prevents any frame where the element appears at old position
    if (modalRef.current) {
      // Set final position directly on element (synchronous)
      modalRef.current.style.top = `${newTop}px`;
      modalRef.current.style.left = `${newLeft}px`;
      // Now safe to clear transform - element is already at final position
      modalRef.current.style.transform = '';
    }
    // Update React state to match DOM (keeps state in sync)
    setModalPosition({
      top: newTop,
      left: newLeft
    });
    setIsDragging(false);
    dragOffsetRef.current = {
      x: 0,
      y: 0
    };
  }, [modalPosition]);
  // Add global mouse event listeners for dragging - OPTIMIZED
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        // Cleanup RAF on unmount
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);
  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const newErrors: {
      title?: string;
      student?: string;
      dueDate?: string;
    } = {};
    if (!title.trim()) {
      newErrors.title = 'Task title is required';
    }
    if (!selectedStudent) {
      newErrors.student = 'Please select a student';
    }
    if (!dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Create task data
    const taskData = {
      id: 'task_' + Date.now().toString(),
      title,
      name: title,
      studentId: selectedStudent,
      studentName: students.find(s => s.id === selectedStudent)?.name || '',
      dueDate,
      deadline: dueDate,
      notes,
      reference,
      status: 'pending',
      teacherId: teacherId,
      createdAt: new Date().toISOString(),
      ...(lessonId && {
        lessonId
      }) // Include lessonId if provided
    };
    // Call onAddTask callback - parent will handle persistence
    if (onAddTask) {
      onAddTask(taskData);
    }
    // Show success message
    toast.success('Task created successfully!');
    // Close modal
    onClose();
  };
  // Check if form is valid
  const isFormValid = () => {
    return title.trim() !== '' && selectedStudent !== '' && dueDate !== '';
  };
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  // Filter students based on search
  const filteredStudents = students.filter(student => student.name.toLowerCase().includes(studentSearch.toLowerCase()) || student.email && student.email.toLowerCase().includes(studentSearch.toLowerCase()));
  // Handle add new student
  const handleAddStudent = async () => {
    if (!newStudent.name.trim()) return;
    if (!teacherId) {
      toast.error('Cannot add student: not logged in');
      return;
    }
    try {
      // Show loading state
      setIsLoadingStudents(true);
      // Prepare student data for Firestore
      const nameParts = newStudent.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const studentData = {
        studentId: `manual-${Date.now()}`,
        name: newStudent.name.trim(),
        firstName: firstName,
        lastName: lastName,
        email: newStudent.email.trim() || '',
        phone: newStudent.phone.trim() || '',
        instrument: '',
        status: 'active' as const,
        notes: ''
      };
      console.log('[ADD STUDENT] Saving to Firestore:', studentData);
      // ✅ CRITICAL FIX: Save to Firestore
      const studentDocId = await addStudentToTeacher(teacherId, studentData);
      console.log('[ADD STUDENT] Saved successfully with ID:', studentDocId);
      // ✅ Refresh student list from Firestore
      const fetchedStudents = await getTeacherStudents(teacherId);
      const activeStudents = fetchedStudents.filter((student: any) => !student.status || student.status === 'active' || student.status === 'trial' || student.status === 'pending');
      setStudents(activeStudents);
      console.log('[ADD STUDENT] Student list refreshed, total:', activeStudents.length);
      // ✅ Find and select the newly created student
      const newlyCreatedStudent = activeStudents.find(s => s.id === studentDocId);
      if (newlyCreatedStudent) {
        setSelectedStudent(newlyCreatedStudent.id);
        console.log('[ADD STUDENT] Auto-selected new student:', newlyCreatedStudent.name);
      } else {
        console.warn('[ADD STUDENT] Could not find newly created student in list');
      }
      // Reset form
      setNewStudent({
        name: '',
        email: '',
        phone: ''
      });
      setIsAddingNewStudent(false);
      setIsStudentDropdownOpen(false);
      // Clear validation errors
      if (errors.student) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.student;
          return newErrors;
        });
      }
      // ✅ Show success message ONLY after Firestore confirms
      toast.success('Student added successfully!');
    } catch (error) {
      console.error('[ADD STUDENT] Error saving to Firestore:', error);
      // ✅ Show clear error message
      const errorMessage = error.message || 'Failed to add student. Please try again.';
      toast.error(errorMessage);
      // Do NOT add student to local state on error
      // Keep the form open so user can retry
    } finally {
      setIsLoadingStudents(false);
    }
  };
  // Handle student selection
  const handleSelectStudent = (studentId: string) => {
    setSelectedStudent(studentId);
    setIsStudentDropdownOpen(false);
    if (errors.student) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors.student;
        return newErrors;
      });
    }
  };
  // Clear selected student
  const handleClearStudent = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStudent('');
  };
  // Get selected student
  const selectedStudentData = students.find(s => s.id === selectedStudent);
  if (!isOpen) return null;
  // Calculate final position
  const finalTop = modalPosition.top + dragOffsetRef.current.y;
  const finalLeft = modalPosition.left + dragOffsetRef.current.x;
  return <div className="fixed inset-0 z-50 overflow-x-hidden overflow-y-auto" data-id="element-391">
      <div className="flex min-h-screen items-center justify-center px-4 py-4 text-center" data-id="element-392">
        {/* Enhanced background overlay with blur */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity z-10" onClick={onClose} aria-hidden="true" data-id="element-393"></div>
        {/* Modal panel */}
        <div ref={modalRef} className={`relative z-20 ${isDark ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'} rounded-xl px-4 pt-5 pb-4 ${isDragging ? 'transition-none' : ''} sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto ${window.innerWidth >= 768 ? 'md:fixed' : ''} ${isDragging ? 'cursor-move' : ''}`} style={{
        ...(window.innerWidth >= 768 ? {
          top: `${modalPosition.top}px`,
          left: `${modalPosition.left}px`
        } : {}),
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: 'pan-y',
        ...(isDark ? {
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        } : {})
      }} onClick={e => e.stopPropagation()} data-id="element-394">
          {/* Header - now draggable */}
          <div className="flex items-center justify-between mb-6 md:cursor-move" onMouseDown={handleDragStart} title={window.innerWidth >= 768 ? 'Drag to move' : undefined} data-id="element-395">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-396">
              Create New Task
            </h3>
            <button onClick={onClose} className={`${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700/50' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'} focus:outline-none rounded-lg p-1.5 transition-all`} aria-label="Close" data-id="element-397">
              <XIcon className="h-5 w-5" data-id="element-398" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-399">
            {/* Task Title */}
            <div data-id="element-400">
              <label htmlFor="title" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-401">
                Task Title*
              </label>
              <input type="text" id="title" ref={titleInputRef} value={title} onChange={e => setTitle(e.target.value)} placeholder="Add task title" className={`w-full rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${errors.title ? 'border-red-500 focus:border-red-500' : isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all px-4 py-2.5`} required data-id="element-402" />
              {errors.title && <p className="mt-1.5 text-sm text-red-400" data-id="element-403">{errors.title}</p>}
            </div>
            {/* Student Selection */}
            <div data-id="element-404">
              <label htmlFor="student" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-405">
                Assign to Student*
              </label>
              <div className="relative" ref={studentDropdownRef} data-id="element-406">
                <div className={`flex items-center justify-between w-full rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${errors.student ? 'border-red-500' : isDark ? 'focus-within:border-[#10B981] focus-within:ring-1 focus-within:ring-[#10B981]/20' : 'focus-within:border-deep-pink focus-within:ring-1 focus-within:ring-deep-pink/20'} px-4 py-2.5 cursor-pointer shadow-sm transition-all`} onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)} data-id="element-407">
                  <div className="flex items-center flex-grow" data-id="element-408">
                    <UserIcon className="h-4 w-4 mr-2.5 flex-shrink-0" style={{
                    color: '#3B82F6'
                  }} data-id="element-409" />
                    {!isStudentDropdownOpen ? <span className={selectedStudentData ? 'text-sm' : `text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-410">
                        {isLoadingStudents ? 'Loading students...' : selectedStudentData ? selectedStudentData.name : 'Select a student'}
                      </span> : <input ref={studentSearchInputRef} type="text" value={studentSearch} onChange={e => setStudentSearch(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Search students..." className={`w-full border-none outline-none ${isDark ? 'bg-transparent text-white' : 'bg-transparent text-gray-900'} placeholder:text-gray-500 focus:ring-0`} data-id="element-411" />}
                  </div>
                  {selectedStudent && !isStudentDropdownOpen && <button type="button" onClick={handleClearStudent} className={`ml-2 p-1 rounded-lg ${isDark ? 'hover:bg-navy-700/50' : 'hover:bg-gray-100'} transition-colors`} data-id="element-412">
                      <XIcon className="h-4 w-4 text-gray-400" data-id="element-413" />
                    </button>}
                  {isStudentDropdownOpen && <SearchIcon className="h-4 w-4 text-gray-400 ml-2" data-id="element-414" />}
                </div>
                {/* Dropdown content */}
                {isStudentDropdownOpen && <div className={`absolute z-10 w-full mt-2 rounded-lg shadow-xl ${isDark ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'}`} style={isDark ? {
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
              } : {}} data-id="element-415">
                    {!isAddingNewStudent && <ul className={`max-h-60 overflow-auto py-1 text-sm ${isDark ? 'text-white' : 'text-gray-700'}`} data-id="element-416">
                        {isLoadingStudents ? <li className="px-4 py-2.5 text-center text-gray-500" data-id="element-417">
                            Loading students...
                          </li> : filteredStudents.length > 0 ? filteredStudents.map(student => <li key={student.id} className={`px-4 py-2.5 cursor-pointer flex items-center justify-between ${student.id === selectedStudent ? isDark ? 'bg-navy-700/50' : 'bg-gray-100' : isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} transition-colors`} onClick={() => handleSelectStudent(student.id)} data-id="element-418">
                              <span className="flex-grow" data-id="element-419">{student.name}</span>
                              {student.email && <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} ml-2`} data-id="element-420">
                                  {student.email}
                                </span>}
                            </li>) : <li className="px-4 py-2.5 text-center text-gray-500" data-id="element-421">
                            No students found
                          </li>}
                        <li className={`px-4 py-2.5 cursor-pointer flex items-center ${isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} text-[#10B981] border-t ${isDark ? 'border-navy-700/50' : 'border-gray-100'} font-medium transition-colors`} onClick={() => setIsAddingNewStudent(true)} data-id="element-422">
                          <PlusIcon className="h-4 w-4 mr-2" data-id="element-423" />
                          <span data-id="element-424">Add new student</span>
                        </li>
                      </ul>}
                    {isAddingNewStudent && <div className={`p-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-425">
                        <h4 className="font-medium mb-3 text-sm" data-id="element-426">
                          Add New Student
                        </h4>
                        <div className="space-y-3" data-id="element-427">
                          <input type="text" value={newStudent.name} onChange={e => setNewStudent({
                      ...newStudent,
                      name: e.target.value
                    })} placeholder="Student name *" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} autoFocus data-id="element-428" />
                          <input type="email" value={newStudent.email} onChange={e => setNewStudent({
                      ...newStudent,
                      email: e.target.value
                    })} placeholder="Email (optional)" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} data-id="element-429" />
                          <input type="tel" value={newStudent.phone} onChange={e => setNewStudent({
                      ...newStudent,
                      phone: e.target.value
                    })} placeholder="Phone (optional)" className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 transition-all`} data-id="element-430" />
                          <div className="flex justify-end space-x-2 pt-1" data-id="element-431">
                            <button type="button" onClick={() => setIsAddingNewStudent(false)} className={`px-4 py-2 text-sm rounded-lg font-medium ${isDark ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} data-id="element-432">
                              Cancel
                            </button>
                            <button type="button" onClick={handleAddStudent} disabled={!newStudent.name.trim()} className={`px-4 py-2 text-sm rounded-lg font-medium border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 transition-colors ${!newStudent.name.trim() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-433">
                              Add Student
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>}
              </div>
              {errors.student && <p className="mt-1.5 text-sm text-red-400" data-id="element-434">{errors.student}</p>}
            </div>
            {/* Due Date */}
            <div data-id="element-435">
              <label htmlFor="dueDate" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-436">
                Due Date*
              </label>
              <div className="relative" data-id="element-437">
                <input type="date" id="dueDate" value={dueDate} onChange={e => setDueDate(e.target.value)} className={`w-full max-w-full min-w-0 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${errors.dueDate ? 'border-red-500 focus:border-red-500' : isDark ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all pl-11 pr-4 py-2.5`} required data-id="element-438" />
                <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-439">
                  <CalendarIcon className="h-4 w-4" style={{
                  color: '#3B82F6'
                }} data-id="element-440" />
                </div>
              </div>
              {errors.dueDate && <p className="mt-1.5 text-sm text-red-400" data-id="element-441">{errors.dueDate}</p>}
            </div>
            {/* Reference */}
            <div data-id="element-442">
              <label htmlFor="reference" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-443">
                Reference (Optional)
              </label>
              <input type="text" id="reference" value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g., Page 42, Exercise 3" className={`w-full rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all px-4 py-2.5`} data-id="element-444" />
            </div>
            {/* Notes */}
            <div data-id="element-445">
              <label htmlFor="notes" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-446">
                Notes (Optional)
              </label>
              <div className="relative" data-id="element-447">
                <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={`w-full rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all pl-11 pr-4 py-2.5`} placeholder="Add any notes about this task..." data-id="element-448"></textarea>
                <div className="absolute top-3.5 left-3.5 pointer-events-none" data-id="element-449">
                  <BookIcon className="h-4 w-4" style={{
                  color: '#9CA3AF'
                }} data-id="element-450" />
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t border-navy-700/30" data-id="element-451">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} focus:outline-none transition-colors`} data-id="element-452">
                Cancel
              </button>
              <button type="submit" className={`px-4 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none flex items-center transition-colors ${!isFormValid() ? 'opacity-50 cursor-not-allowed border-gray-600 text-gray-600' : isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} disabled={!isFormValid()} data-id="element-453">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-454" />
                Save Task
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Add styles to fix date input visibility */}
      <style jsx global data-id="element-455">{`
        input[type='date']::-webkit-calendar-picker-indicator {
          opacity: 0;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          cursor: pointer;
        }
        input[type='date'] {
          color: ${isDark ? 'white' : '#1f2937'} !important;
        }

        /* Mobile-only: Prevent date input overflow */
        @media (max-width: 767px) {
          input[type='date'] {
            min-width: 0 !important;
            max-width: 100% !important;
            -webkit-appearance: none !important;
            -moz-appearance: textfield !important;
            appearance: none !important;
          }
        }

        input:focus,
        textarea:focus {
          caret-color: ${isDark ? 'white' : '#1f2937'} !important;
        }
      `}</style>
    </div>;
};